import appSettings from '../../appSettings';
import { fetchIDSToken } from './proxyServices';
import { makeServiceCall, logGenericError } from '../utilities/ServiceHelperUtil';
import { getNullSafeProperty} from '../../common/utilities/CommonHelperUtil';
import { v4 as uuidv4 } from 'uuid';

const normalizePrismId = (prismId) => prismId ? prismId.toLowerCase() : prismId;

const fetchAgentPortalAlerts = async (filterBy, token) => {
    const filterField = filterBy ?? '';
    const url = `agent-portal-alerts?_fields=${filterField}`;
    const eventName = 'fetchAgentPortalAlerts';
    try {
        return await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'GET',
            token: token
        });
    } catch (error) {
        return error;
    }
}

const fetchAgentPortalProfile = async (prismId, domain, token) => {
    const eventName = 'fetchAgentPortalProfile';
    const url = `agents/${normalizePrismId(prismId)}/profile?domain=${domain}`;
    try {
        const data = await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'GET',
            token: token
        });

        return data.agentPortalProfile
    } catch (error) {
        logGenericError(url, eventName, error)
    }
}

const fetchBackgroundCheck = async (prismId, name, email, token) => {
    const eventName = 'fetchBackgroundCheck';
    const url = `verify-background-check?prismId=${normalizePrismId(prismId)}&name=${name}&email=${email}`;
    try {
        const data = await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'GET',
            token: token
        });
  
        if (data.status === "ERROR") {
            throw new Error('background check returned status: ' + data.status);
        }
  
        return data;
    } catch (error) {
        logGenericError(url, eventName, error)
    }
};

const fetchSubproducers = async (chAsgnId, prismId, token) => {
    const eventName = 'fetchSubproducers';
    const url = `subproducers?chAsgnId=${chAsgnId}&prismId=${normalizePrismId(prismId)}`;
    try {
        const data =  await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'GET',
            token: token,
            data: normalizePrismId(prismId)
        });
        return data?.subProducers;
    } catch (error) {
        logGenericError(url, eventName, error, true);
        const genericError = `Error in fetchSubproducers service - Username: ${normalizePrismId(prismId)} - Event: Get Subproducers - Description: An error occurred while getting subproducer data - Error Code: 500 - Error Description: ${error}`
        if(error.message && error.message !== ''){
            throw new Error(error);
        }
        throw new Error(genericError);
    }
}

const fetchProducts = async (userId, state, effectiveDate, agentCode, subproducerCode, token) => {
    const eventName = 'fetchProducts';
    const url = `selectable-products?userId=${userId}&agentCode=${agentCode}&subproducerCode=${subproducerCode}&state=${state}&effectiveDate=${effectiveDate}`;
    try {
        const data = await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'GET',
            token: token,
            extraHeaders: { environment: appSettings.ENVIRONMENT },
        });

        if (data?.status === 'failure') {
            throw new Error(data.message);
        }
        if (data?.portalProducts?.length === 0) {
            throw new Error('No Products and Policy Types Found');
        }
        return data.portalProducts;
    } catch (error) {
        // this requires a custom error based on the potential response
        const statusCode = getNullSafeProperty(error, ['response', 'data', 'statusCode']);
        const responseMsg = getNullSafeProperty(error, ['response', 'data', 'messages']);
        const errorMsg = (responseMsg && responseMsg.messages[0] !== undefined) ? error.response.data.messages[0].description : error.message;
        logGenericError(url, eventName, error, true);
        const genericError = `Error in fetchProducts service - Username: ${userId} - Event: Get Agent/Sub Products - Description: An error occurred while fetching products for the agent/sub combo - Error Code: 500 - Error Description: ${errorMsg}`;
        // Decide whether to rethrow a generic or specific error based on the presence of statusCode
        throw new Error((!statusCode || statusCode.length === 0) && error.message && error.message !== '' && !error.message.includes('Network Error') ? error : genericError);
    }
};

const saveBackgroundCheck = async (answers, prismId, token) => { 
    const eventName = 'saveBackgroundCheck';
    const url = `save-backgroundcheck-answers?userId=${normalizePrismId(prismId)}`;
    try {
        return await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'POST',
            token: token,
            data: answers
        });
    } catch (error) {
        logGenericError(url, eventName, error);
    }
}

const createEnrollment = async (enrollmentData, token) => {
    const eventName = 'createEnrollment';
    const url = `enrollment`;
    try {
        return await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'POST',
            token: token,
            data: enrollmentData
        });
    } catch (error) {
        logGenericError(url, eventName, error);
    }
}

const createNewUser = async (userData, token) => {
    const eventName = 'createNewUser';
    const url = `enroll-user`;
    try {
        return await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'POST',
            token: token,
            data: userData
        });
    } catch (error) {
        logGenericError(url, eventName, error);
    }
}

const createPreference = async (preferenceData, token) => {
    const eventName = 'createPreference';
    const url = `preference`;
    try {
        return await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'POST',
            token: token,
            data: preferenceData
        });
    } catch (error) {
        logGenericError(url, eventName, error);
    }
}

const updateContact = async (userData, token) => {
    const eventName = 'updateContact';
    const url = `contact`;
    try {
        return await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'POST',
            token: token,
            data: userData
        });
    } catch (error) {
        logGenericError(url, eventName, error);
    }
}

const fetchPolicyData = async (criteria) => {
    const eventName = 'fetchPolicyData';
    const url = `enhanced-search`;
    const token = await fetchIDSToken();
    try {
        const data = await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'POST',
            token: token,
            data: criteria,
            extraHeaders: { 'Correlation-Id': uuidv4() },
        });
        return data.policies;
    } catch (error) {
        if(error.response && error.response.data) {
            return [''];
        }
        logGenericError(url, eventName, error, true);
        throw error;
    }
}

const updateEnrollmentProperty = async (guid, property, newValue, token) => {
    if(!guid) { 
        return; 
    }
    const eventName = 'updateEnrollmentProperty';
    const url = `enrollment/${guid}/${property}`;
    try {
        return await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'PUT',
            token: token,
            data: {value: newValue}
        });
    } catch (error) {
        logGenericError(url, eventName, error);
    }
}

const setPrimaryEnrollment = async (previousPrimary, newPrimary, token) => {
    const eventName = 'setPrimaryEnrollment';
    const url = `set-primary-enrollment?previousPrimaryEnrollmentId=${previousPrimary}&newPrimaryEnrollmentId=${newPrimary}`;
    try {
        return await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'PUT',
            token: token,
        });
    } catch (error) {
        logGenericError(url, eventName, error);
    }
}

const deleteEnrollment = async (userId, token) => {
    const eventName = 'deleteEnrollment';
    const url = `enrollment/${userId}`;
    try {
        return await makeServiceCall({
            eventName: eventName,
            url: url,
            method: 'DELETE',
            token: token
        });
    } catch (error) {
        logGenericError(url, eventName, error)
    }
}


export { fetchAgentPortalAlerts, fetchBackgroundCheck, fetchAgentPortalProfile, fetchSubproducers, fetchProducts, createEnrollment, createNewUser, createPreference, updateEnrollmentProperty, updateContact, setPrimaryEnrollment, deleteEnrollment, saveBackgroundCheck, fetchPolicyData };
